import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import Icon from 'components/AppIcon';
import ProgressHeader from 'components/ui/ProgressHeader';
import ResultsNavigation from 'components/ui/ResultsNavigation';
import WellnessScoreCard from './components/WellnessScoreCard';
import RecommendationCard from './components/RecommendationCard';
import KeyFindingsSection from './components/KeyFindingsSection';
import ExportOptions from './components/ExportOptions';
import FollowUpOptions from './components/FollowUpOptions';

const AssessmentResults = () => {
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const [showExportModal, setShowExportModal] = useState(false);
  const [showFollowUpModal, setShowFollowUpModal] = useState(false);

  // Mock assessment results data
  const assessmentResults = {
    overallScore: 72,
    scoreCategory: "Moderate Wellness",
    completionDate: new Date().toISOString(),
    totalQuestions: 45,
    answeredQuestions: 45,
    riskLevel: "moderate",
    keyFindings: [
      {
        id: 1,
        category: "Sleep Quality",
        status: "concern",
        finding: "Your sleep patterns show significant disruption with frequent wake-ups and difficulty falling asleep.",
        impact: "Poor sleep quality can significantly affect mood, immune function, and overall wellness."
      },
      {
        id: 2,
        category: "Anxiety Levels",
        status: "moderate",
        finding: "You\'re experiencing moderate anxiety levels, particularly related to health concerns and social situations.",
        impact: "Managing anxiety through proven techniques can improve daily functioning and quality of life."
      },
      {
        id: 3,
        category: "Physical Activity",
        status: "positive",
        finding: "You maintain regular physical activity which is excellent for both mental and physical health.",
        impact: "Continue your current exercise routine as it\'s a strong protective factor for overall wellness."
      },
      {
        id: 4,
        category: "Social Support",
        status: "concern",
        finding: "Limited social connections and support systems were identified in your responses.",
        impact: "Strong social connections are crucial for mental health resilience and recovery."
      }
    ],
    recommendations: {
      immediate: [
        {
          id: 1,
          title: "Sleep Hygiene Improvement",
          priority: "high",
          description: "Implement a consistent bedtime routine and create a sleep-conducive environment.",
          actions: [
            "Set a regular bedtime and wake time, even on weekends",
            "Avoid screens 1 hour before bedtime",
            "Keep bedroom cool, dark, and quiet",
            "Try relaxation techniques like deep breathing or meditation"
          ],
          timeframe: "Start tonight",
          resources: [
            { name: "Sleep Foundation Guide", url: "#", type: "website" },
            { name: "Calm App - Sleep Stories", url: "#", type: "app" }
          ]
        },
        {
          id: 2,
          title: "Anxiety Management Techniques",
          priority: "high",
          description: "Learn and practice evidence-based anxiety reduction strategies.",
          actions: [
            "Practice 4-7-8 breathing technique daily",
            "Try progressive muscle relaxation",
            "Use grounding techniques during anxious moments",
            "Consider mindfulness meditation for 10 minutes daily"
          ],
          timeframe: "Start today",
          resources: [
            { name: "Headspace - Anxiety Course", url: "#", type: "app" },
            { name: "Anxiety and Depression Association", url: "#", type: "website" }
          ]
        }
      ],
      lifestyle: [
        {
          id: 3,
          title: "Social Connection Building",
          priority: "medium",
          description: "Gradually increase social interactions and build support networks.",
          actions: [
            "Schedule one social activity per week",
            "Join online or local interest groups",
            "Reach out to one friend or family member daily",
            "Consider volunteering for community organizations"
          ],
          timeframe: "Over next 2-4 weeks",
          resources: [
            { name: "Meetup - Local Groups", url: "#", type: "website" },
            { name: "VolunteerMatch", url: "#", type: "website" }
          ]
        },
        {
          id: 4,
          title: "Stress Reduction Practices",
          priority: "medium",
          description: "Incorporate regular stress management into your daily routine.",
          actions: [
            "Practice yoga or tai chi 2-3 times per week",
            "Spend time in nature daily, even if just 15 minutes",
            "Keep a gratitude journal",
            "Limit news consumption to specific times"
          ],
          timeframe: "Implement gradually over 1 month",
          resources: [
            { name: "Yoga with Adriene", url: "#", type: "youtube" },
            { name: "Five Minute Journal", url: "#", type: "app" }
          ]
        }
      ],
      professional: [
        {
          id: 5,
          title: "Mental Health Professional Consultation",
          priority: "recommended",
          description: "Consider speaking with a mental health professional for personalized support.",
          actions: [
            "Research therapists in your area specializing in anxiety",
            "Check if your insurance covers mental health services",
            "Consider telehealth options for convenience",
            "Prepare questions about your specific concerns"
          ],
          timeframe: "Within 2-3 weeks",
          resources: [
            { name: "Psychology Today Therapist Finder", url: "#", type: "website" },
            { name: "BetterHelp Online Therapy", url: "#", type: "website" },
            { name: "Crisis Text Line: Text HOME to 741741", url: "#", type: "emergency" }
          ]
        },
        {
          id: 6,
          title: "Primary Care Follow-up",
          priority: "recommended",
          description: "Discuss your wellness concerns with your primary care physician.",
          actions: [
            "Schedule annual physical if overdue",
            "Discuss sleep and anxiety concerns",
            "Review any physical symptoms",
            "Ask about referrals to specialists if needed"
          ],
          timeframe: "Within 1 month",
          resources: [
            { name: "Prepare for Your Doctor Visit", url: "#", type: "checklist" }
          ]
        }
      ]
    }
  };

  const handleExport = (format) => {
    setIsLoading(true);
    // Simulate export process
    setTimeout(() => {
      setIsLoading(false);
      console.log(`Exporting results as ${format}`);
    }, 2000);
  };

  const handleEmail = async (emailAddress) => {
    setIsLoading(true);
    // Simulate email sending
    return new Promise((resolve) => {
      setTimeout(() => {
        setIsLoading(false);
        resolve();
      }, 2000);
    });
  };

  const handlePrint = () => {
    window.print();
  };

  const handleRetake = () => {
    navigate('/assessment-welcome-introduction');
  };

  const handleScheduleFollowup = () => {
    setShowFollowUpModal(true);
  };

  const getScoreColor = (score) => {
    if (score >= 80) return 'text-success';
    if (score >= 60) return 'text-warning';
    return 'text-error';
  };

  const getScoreBackground = (score) => {
    if (score >= 80) return 'bg-success-50 border-success-100';
    if (score >= 60) return 'bg-warning-50 border-warning-100';
    return 'bg-error-50 border-error-100';
  };

  return (
    <div className="min-h-screen bg-background">
      <ProgressHeader 
        currentStep={3}
        totalSteps={3}
        currentStepTitle="Results"
        completionPercentage={100}
      />

      <div className="pt-32 pb-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Header Section */}
          <div className="text-center mb-8">
            <div className="flex items-center justify-center w-16 h-16 bg-success-100 rounded-full mx-auto mb-4">
              <Icon name="CheckCircle" size={32} color="var(--color-success)" />
            </div>
            <h1 className="text-3xl font-heading font-semibold text-text-primary mb-2">
              Assessment Complete
            </h1>
            <p className="text-lg text-text-secondary max-w-2xl mx-auto">
              Your personalized wellness insights and recommendations are ready. Review your results and take the next steps toward better health.
            </p>
          </div>

          {/* Main Content Grid */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* Left Column - Summary & Score */}
            <div className="lg:col-span-1 space-y-6">
              <WellnessScoreCard 
                score={assessmentResults.overallScore}
                category={assessmentResults.scoreCategory}
                riskLevel={assessmentResults.riskLevel}
                completionDate={assessmentResults.completionDate}
                totalQuestions={assessmentResults.totalQuestions}
              />

              {/* Quick Actions */}
              <div className="card space-y-4">
                <h3 className="text-lg font-heading font-semibold text-text-primary">
                  Quick Actions
                </h3>
                <div className="space-y-3">
                  <button
                    onClick={() => setShowExportModal(true)}
                    className="w-full flex items-center space-x-3 px-4 py-3 bg-primary text-white rounded-lg hover:bg-primary-700 transition-all duration-200"
                  >
                    <Icon name="Download" size={20} />
                    <span className="font-medium">Download Results</span>
                  </button>
                  
                  <button
                    onClick={handlePrint}
                    className="w-full flex items-center space-x-3 px-4 py-3 bg-surface-secondary border border-border rounded-lg text-text-primary hover:bg-border-light transition-all duration-200"
                  >
                    <Icon name="Printer" size={20} />
                    <span className="font-medium">Print Results</span>
                  </button>

                  <button
                    onClick={handleScheduleFollowup}
                    className="w-full flex items-center space-x-3 px-4 py-3 bg-accent-50 border border-accent-100 text-accent-600 rounded-lg hover:bg-accent-100 transition-all duration-200"
                  >
                    <Icon name="Calendar" size={20} />
                    <span className="font-medium">Schedule Follow-up</span>
                  </button>
                </div>
              </div>

              {/* Emergency Resources */}
              <div className="card bg-error-50 border-error-100">
                <div className="flex items-start space-x-3">
                  <Icon name="AlertTriangle" size={20} color="var(--color-error)" className="mt-1 flex-shrink-0" />
                  <div>
                    <h4 className="font-medium text-error mb-2">
                      Need Immediate Help?
                    </h4>
                    <div className="text-sm text-error space-y-1">
                      <p>Crisis Text Line: Text HOME to 741741</p>
                      <p>National Suicide Prevention Lifeline: 988</p>
                      <p>Emergency Services: 911</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Right Column - Detailed Results */}
            <div className="lg:col-span-2 space-y-8">
              {/* Key Findings */}
              <KeyFindingsSection findings={assessmentResults.keyFindings} />

              {/* Recommendations */}
              <div className="space-y-6">
                <h2 className="text-2xl font-heading font-semibold text-text-primary">
                  Your Personalized Recommendations
                </h2>

                {/* Immediate Actions */}
                <div>
                  <div className="flex items-center space-x-2 mb-4">
                    <div className="w-3 h-3 bg-error rounded-full"></div>
                    <h3 className="text-lg font-heading font-medium text-text-primary">
                      Immediate Actions (Start Today)
                    </h3>
                  </div>
                  <div className="space-y-4">
                    {assessmentResults.recommendations.immediate.map((rec) => (
                      <RecommendationCard key={rec.id} recommendation={rec} />
                    ))}
                  </div>
                </div>

                {/* Lifestyle Modifications */}
                <div>
                  <div className="flex items-center space-x-2 mb-4">
                    <div className="w-3 h-3 bg-warning rounded-full"></div>
                    <h3 className="text-lg font-heading font-medium text-text-primary">
                      Lifestyle Modifications (Next 2-4 Weeks)
                    </h3>
                  </div>
                  <div className="space-y-4">
                    {assessmentResults.recommendations.lifestyle.map((rec) => (
                      <RecommendationCard key={rec.id} recommendation={rec} />
                    ))}
                  </div>
                </div>

                {/* Professional Resources */}
                <div>
                  <div className="flex items-center space-x-2 mb-4">
                    <div className="w-3 h-3 bg-primary rounded-full"></div>
                    <h3 className="text-lg font-heading font-medium text-text-primary">
                      Professional Resources (Consider Within 1 Month)
                    </h3>
                  </div>
                  <div className="space-y-4">
                    {assessmentResults.recommendations.professional.map((rec) => (
                      <RecommendationCard key={rec.id} recommendation={rec} />
                    ))}
                  </div>
                </div>
              </div>

              {/* Disclaimer */}
              <div className="card bg-primary-50 border-primary-100">
                <div className="flex items-start space-x-3">
                  <Icon name="Info" size={20} color="var(--color-primary)" className="mt-1 flex-shrink-0" />
                  <div>
                    <h4 className="font-medium text-primary mb-2">
                      Important Disclaimer
                    </h4>
                    <p className="text-sm text-primary-600">
                      This assessment is for informational purposes only and does not constitute medical advice, diagnosis, or treatment. Always consult with qualified healthcare professionals for medical concerns. If you're experiencing a mental health emergency, please contact emergency services immediately.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Bottom Navigation */}
          <div className="mt-12">
            <ResultsNavigation
              onExport={handleExport}
              onEmail={handleEmail}
              onPrint={handlePrint}
              onRetake={handleRetake}
              onScheduleFollowup={handleScheduleFollowup}
              resultsData={assessmentResults}
              isLoading={isLoading}
            />
          </div>

          {/* Navigation Links */}
          <div className="mt-8 flex flex-col sm:flex-row items-center justify-center space-y-4 sm:space-y-0 sm:space-x-6">
            <Link
              to="/assessment-welcome-introduction"
              className="flex items-center space-x-2 text-text-secondary hover:text-primary transition-colors duration-200"
            >
              <Icon name="RotateCcw" size={16} />
              <span>Take Assessment Again</span>
            </Link>
            
            <Link
              to="/multi-step-assessment-form"
              className="flex items-center space-x-2 text-text-secondary hover:text-primary transition-colors duration-200"
            >
              <Icon name="Edit" size={16} />
              <span>Review My Responses</span>
            </Link>
          </div>
        </div>
      </div>

      {/* Export Modal */}
      {showExportModal && (
        <ExportOptions
          onClose={() => setShowExportModal(false)}
          onExport={handleExport}
          isLoading={isLoading}
        />
      )}

      {/* Follow-up Modal */}
      {showFollowUpModal && (
        <FollowUpOptions
          onClose={() => setShowFollowUpModal(false)}
          resultsData={assessmentResults}
        />
      )}
    </div>
  );
};

export default AssessmentResults;